import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LinkText from './LinkText';

function QuicksortModal({ show, currentComparison, progress, onChoice, onCancel }) {
    const { t } = useTranslation(['voting']);
    const [animatingOptions, setAnimatingOptions] = useState({ itemA: false, itemB: false });
    const previousItemsRef = useRef({ itemA: null, itemB: null });
    const modalBodyRef = useRef(null);
    const modalDialogRef = useRef(null);
    const upperOptionRef = useRef(null);
    const lowerOptionRef = useRef(null);
    const isFirstComparisonRef = useRef(true);
    const scrollPositionRef = useRef(0);

    // Handle choice and preserve scroll position
    const handleChoice = (choice) => {
        // Capture scroll position from the actual scrollable container
        let currentScrollY = 0;

        // First check the modal container (the actual scrollable element)
        const modalScrollContainer = document.querySelector('.modal.show');
        if (modalScrollContainer && modalScrollContainer.scrollTop > 0) {
            currentScrollY = modalScrollContainer.scrollTop;
        } else if (modalBodyRef.current && modalBodyRef.current.scrollTop > 0) {
            currentScrollY = modalBodyRef.current.scrollTop;
        } else {
            // Fallback to document scroll
            currentScrollY = window.pageYOffset ||
                            document.documentElement.scrollTop ||
                            document.body.scrollTop || 0;
        }

        scrollPositionRef.current = currentScrollY;

        // Also store in sessionStorage as backup
        sessionStorage.setItem('quicksortScrollPosition', currentScrollY.toString());

        onChoice(choice);
    };

    // Animation effect when currentComparison changes
    useEffect(() => {
        if (currentComparison) {
            const currentItemA = currentComparison.itemA;
            const currentItemB = currentComparison.itemB;
            const previousItems = previousItemsRef.current;

            if (previousItems.itemA !== null || previousItems.itemB !== null) {
                // Determine which options changed by comparing individual items
                const itemAChanged = JSON.stringify(currentItemA) !== JSON.stringify(previousItems.itemA);
                const itemBChanged = JSON.stringify(currentItemB) !== JSON.stringify(previousItems.itemB);

                if (itemAChanged || itemBChanged) {
                    // Start animation for only the changed options
                    setAnimatingOptions({
                        itemA: itemAChanged,
                        itemB: itemBChanged
                    });

                    // Clear animations after duration
                    const timer = setTimeout(() => {
                        setAnimatingOptions({ itemA: false, itemB: false });
                        isFirstComparisonRef.current = false;
                    }, 300);

                    // Store current items as previous for next comparison
                    previousItemsRef.current = { itemA: currentItemA, itemB: currentItemB };
                    return () => clearTimeout(timer);
                }
            } else {
                // First comparison, animate both
                setAnimatingOptions({ itemA: true, itemB: true });
                const timer = setTimeout(() => {
                    setAnimatingOptions({ itemA: false, itemB: false });
                    isFirstComparisonRef.current = false;
                }, 300);
                previousItemsRef.current = { itemA: currentItemA, itemB: currentItemB };
                return () => clearTimeout(timer);
            }

            // Update previous items even if no animation
            previousItemsRef.current = { itemA: currentItemA, itemB: currentItemB };
        }
    }, [currentComparison]);

    // Restore scroll position with iOS Safari compatibility
    useLayoutEffect(() => {
        if (currentComparison && !isFirstComparisonRef.current) {
            // Try to restore from ref first, then sessionStorage as backup
            const savedScrollY = scrollPositionRef.current ||
                                parseInt(sessionStorage.getItem('quicksortScrollPosition') || '0', 10);

            if (savedScrollY > 0) {
                // iOS Safari-compatible scroll restoration with multiple attempts
                const restoreScroll = (attempts = 0) => {
                    const maxAttempts = 3;

                    // First, try to restore scroll position on the modal container (the actual scrollable element)
                    const modalContainer = document.querySelector('.modal.show');
                    if (modalContainer) {
                        modalContainer.scrollTop = savedScrollY;

                        // Check if scroll was actually applied to modal container
                        const currentScroll = modalContainer.scrollTop;

                        // If scroll wasn't applied and we haven't reached max attempts, try again
                        if (Math.abs(currentScroll - savedScrollY) > 5 && attempts < maxAttempts) {
                            requestAnimationFrame(() => restoreScroll(attempts + 1));
                        }
                    } else if (modalBodyRef.current) {
                        modalBodyRef.current.scrollTop = savedScrollY;

                        // Check if scroll was actually applied to modal body
                        const currentScroll = modalBodyRef.current.scrollTop;

                        // If scroll wasn't applied and we haven't reached max attempts, try again
                        if (Math.abs(currentScroll - savedScrollY) > 5 && attempts < maxAttempts) {
                            requestAnimationFrame(() => restoreScroll(attempts + 1));
                        }
                    } else {
                        // Fallback to document scroll if modal elements not available
                        window.scrollTo(0, savedScrollY);
                        document.documentElement.scrollTop = savedScrollY;
                        document.body.scrollTop = savedScrollY;

                        // Check if scroll was actually applied
                        const currentScroll = window.pageYOffset ||
                                            document.documentElement.scrollTop ||
                                            document.body.scrollTop;

                        // If scroll wasn't applied and we haven't reached max attempts, try again
                        if (Math.abs(currentScroll - savedScrollY) > 5 && attempts < maxAttempts) {
                            requestAnimationFrame(() => restoreScroll(attempts + 1));
                        }
                    }
                };

                // Start scroll restoration immediately
                restoreScroll();

                // Also try after a short delay as fallback for iOS Safari
                setTimeout(() => restoreScroll(), 10);
            }
        }
    }, [currentComparison]);

    // Handle keyboard events
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!show || !currentComparison) return;
            
            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    handleChoice(currentComparison.itemA); // Upper item preferred
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    handleChoice(currentComparison.itemB); // Lower item preferred
                    break;
                case 'Escape':
                    event.preventDefault();
                    onCancel();
                    break;
                default:
                    break;
            }
        };

        if (show) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [show, currentComparison, handleChoice, onCancel]);

    // Cleanup when modal closes
    useEffect(() => {
        if (!show) {
            sessionStorage.removeItem('quicksortScrollPosition');
            scrollPositionRef.current = 0;
            isFirstComparisonRef.current = true;
        }
    }, [show]);

    if (!show || !currentComparison) return null;

    const { itemA, itemB } = currentComparison;
    const { current, estimated } = progress;

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg" ref={modalDialogRef}>
                <div className="modal-content quicksort-modal">
                    <div className="modal-header">
                        <h5 className="modal-title">{t('voting:quicksort.title')}</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onCancel}
                        ></button>
                    </div>
                    
                    <div className="modal-body" ref={modalBodyRef}>
                        <div className="quicksort-progress mb-4">
                            <div className="progress">
                                <div 
                                    className="progress-bar bg-primary" 
                                    role="progressbar" 
                                    style={{ width: `${estimated > 0 ? (current / estimated) * 100 : 0}%` }}
                                ></div>
                            </div>
                            <small className="text-muted mt-1 d-block">
                                {t('voting:quicksort.progress', { current: current + 1, estimated })}
                            </small>
                        </div>

                        <div className="quicksort-instruction mb-4 text-center">
                            <h6 className="mb-2">{t('voting:quicksort.instruction')}</h6>
                            <p className="text-muted mb-0">
                                {t('voting:quicksort.instructionHelp')}
                            </p>
                        </div>

                        <div className="quicksort-comparison">
                            <button
                                ref={upperOptionRef}
                                className={`quicksort-option-card w-100 mb-3 ${animatingOptions.itemA ? 'quicksort-option-changing' : ''}`}
                                onClick={(event) => {
                                    // Don't trigger option selection if user clicked on a link
                                    if (event.target.tagName === 'A' || event.target.closest('a')) {
                                        return;
                                    }
                                    handleChoice(itemA);
                                }}
                                type="button"
                            >
                                <div className="quicksort-option-name">
                                    <LinkText text={itemA.name} showImagePreviews={true} />
                                </div>
                                <div className="quicksort-arrow-hint">
                                    {t('voting:quicksort.upperHint')}
                                </div>
                            </button>
                            
                            <button
                                ref={lowerOptionRef}
                                className={`quicksort-option-card w-100 ${animatingOptions.itemB ? 'quicksort-option-changing' : ''}`}
                                onClick={(event) => {
                                    // Don't trigger option selection if user clicked on a link
                                    if (event.target.tagName === 'A' || event.target.closest('a')) {
                                        return;
                                    }
                                    handleChoice(itemB);
                                }}
                                type="button"
                            >
                                <div className="quicksort-option-name">
                                    <LinkText text={itemB.name} showImagePreviews={true} />
                                </div>
                                <div className="quicksort-arrow-hint">
                                    {t('voting:quicksort.lowerHint')}
                                </div>
                            </button>
                        </div>

                        <div className="quicksort-help mt-4">
                            <div className="row">
                                <div className="col-md-6">
                                    <small className="text-muted">
                                        <strong>{t('voting:quicksort.keyboardShortcuts')}</strong><br/>
                                        {t('voting:quicksort.keyUp')}<br/>
                                        {t('voting:quicksort.keyDown')}<br/>
                                        {t('voting:quicksort.keyEscape')}
                                    </small>
                                </div>
                                <div className="col-md-6 text-md-end">
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={onCancel}
                                    >
                                        {t('voting:quicksort.cancelSorting')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QuicksortModal;